<template>
  <div :class="componentClass.root">
    <PmMyRequestsListPure
      :is-loading="isLoading"
      :requests="requestsNormalized"
      @open-details="(payload) => emit('openDetails', payload)"
      @abort="(payload) => emit('abort', payload)"
      @request-cancel-leave-request="
        (payload) => emit('requestCancelLeaveRequest', payload)
      "
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useComponentClass } from '@thomasaull-shared/composables'

import PmMyRequestsListPure, {
  type Props as PropsMyRequestsListPure,
  type Emits as EmitsMyRequestsListPure,
} from '@/components/PmMyRequests/PmMyRequestsListPure.vue'
import { useQuery } from '@vue/apollo-composable'
import { MyRequestsListDocument } from '../../../generated/graphql'
import { parseServerDateString } from '@/utilities/date'
import { LEAVE_REQUEST_STATUS_LOOKUP } from '@/constants/leaveRequest'
import { POLLING_INTERVAL } from '@/constants/persoplan'
import { EXTERNAL_SERVICE_REQUEST_STATUS_LOOKUP } from '@/constants/externalServiceRequest'
import { useCurrentUser } from '@/composition/useCurrentUser'

export type Emits = {
  openDetails: EmitsMyRequestsListPure['openDetails']
  abort: EmitsMyRequestsListPure['abort']
  requestCancelLeaveRequest: EmitsMyRequestsListPure['requestCancelLeaveRequest']
}

const emit = defineEmits<Emits>()

const currentUser = useCurrentUser()
const componentClass = useComponentClass()

const requests = useQuery(
  MyRequestsListDocument,
  () => {
    const addressId = currentUser.value?.address.id
    if (!addressId) throw new Error('addressId is undefined')
    return { addressId }
  },
  () => ({
    enabled: !!currentUser.value?.address.id,
    pollInterval: POLLING_INTERVAL * 1_000,
    notifyOnNetworkStatusChange: true,
  })
)

const isLoading = computed(() => requests.loading.value)

const requestsNormalized = computed(() => {
  const result: PropsMyRequestsListPure['requests'] = []
  if (!requests.result.value) return result

  // Leave Requests
  requests.result.value.leaveRequests?.forEach((leaveRequest) => {
    if (!leaveRequest) return

    const startDate = parseServerDateString(leaveRequest.startDate)
    const endDate = parseServerDateString(leaveRequest.endDate)
    const createdDate = parseServerDateString(leaveRequest.created)

    if (!startDate || !endDate || !createdDate)
      throw new Error('startDate, endDate or createdDate is undefined')

    const type = leaveRequest.type
    if (!type) throw new Error('type is undefined')

    const item: (typeof result)[0] = {
      id: leaveRequest.id,
      startDate,
      endDate,
      createdDate,
      status: LEAVE_REQUEST_STATUS_LOOKUP[leaveRequest.state],
      type,
      requestType: 'leaveRequest',
    }

    result.push(item)
  })

  // External Service Requests
  requests.result.value.externalServiceRequests?.forEach(
    (externalServiceRequest) => {
      if (!externalServiceRequest) return

      const startDate = parseServerDateString(externalServiceRequest.startDate)
      const endDate = parseServerDateString(externalServiceRequest.endDate)
      const createdDate = parseServerDateString(externalServiceRequest.created)

      if (!startDate || !endDate || !createdDate)
        throw new Error('startDate, endDate or createdDate is undefined')

      const item: (typeof result)[0] = {
        id: externalServiceRequest.id,
        startDate,
        endDate,
        createdDate,
        status:
          EXTERNAL_SERVICE_REQUEST_STATUS_LOOKUP[externalServiceRequest.state],
        requestType: 'externalServiceRequest',
      }

      result.push(item)
    }
  )

  // Expense Reports
  requests.result.value.expenseReports?.forEach((expenseReport) => {
    if (!expenseReport) return

    const date = parseServerDateString(expenseReport.date)
    const createdDate = parseServerDateString(expenseReport.created)

    if (!date || !createdDate)
      throw new Error('date or createdDate is undefined')

    const item: (typeof result)[0] = {
      id: expenseReport.id,
      date: date,
      createdDate,
      requestType: 'expenseReport',
    }

    result.push(item)
  })

  return result
})
</script>

<style lang="scss">
.PmMyRequestsList {
  $block: &;
}
</style>
